// audio.js — the machine beeper as a WebAudio square wave.
//
// The MS-2401H drives a piezo beeper; vm86_beeper() reports [enabled, freq].
// We mirror that with a single persistent oscillator whose gain we gate, so
// there are no clicks from start/stop churn. WebAudio requires a user gesture
// to start, so the context is created lazily on the first unlock() call
// (wired to the first pointer/key event by app.js). A mute toggle and a volume
// control sit on top; when muted the gain stays at zero regardless of state.

export class Beeper {
  constructor() {
    this.ctx = null;
    this.osc = null;
    this.gain = null;
    this.enabled = false;      // machine says the beeper is sounding
    this.freq = 1000;          // machine-reported frequency (Hz)
    this.muted = false;        // user mute toggle
    this.volume = 0.08;        // 0..0.2 peak gain (square waves are loud)
    this._unlocked = false;
    this._lastEvents = undefined; // last-seen beep ON-edge count (from the bridge)
  }

  // Create/resume the AudioContext. Safe to call repeatedly; only the first
  // call inside a user gesture actually unlocks audio in the browser.
  unlock() {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return false;
    if (!this.ctx) {
      this.ctx = new AudioCtx();
      this.osc = this.ctx.createOscillator();
      this.osc.type = 'square';
      this.gain = this.ctx.createGain();
      this.gain.gain.value = 0;
      this.osc.connect(this.gain);
      this.gain.connect(this.ctx.destination);
      this.osc.frequency.value = this.freq;
      this.osc.start();
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
    this._unlocked = true;
    this._apply();
    return true;
  }

  get isUnlocked() { return this._unlocked && !!this.ctx; }

  setMuted(m) { this.muted = !!m; this._apply(); }
  setVolume(v) {
    this.volume = Math.min(0.2, Math.max(0, Number(v) || 0));
    this._apply();
  }

  // Called each frame with the machine's beeper snapshot. `events` is the
  // bridge's monotonic count of beep ON edges; a beep whose on/off pair fell
  // between two frames shows up here as a bump in `events` while `enabled` is
  // already back to false — the level gate would miss it, so we synthesize a
  // short blip instead. Sustained tones (enabled held across frames) are gated
  // by _apply() as before.
  update(enabled, freq, events, eventFreq) {
    this.enabled = !!enabled;
    if (Number.isFinite(freq) && freq > 0) this.freq = freq;

    // Diff the ON-edge counter. max(0, …) absorbs the counter resetting to 0 on
    // a power-cycle / warm reset (the wasm module zeroes it; this Beeper object
    // survives). undefined on the first frame => no diff, no spurious blip.
    let newEdges = 0;
    if (Number.isFinite(events)) {
      if (this._lastEvents !== undefined) newEdges = Math.max(0, events - this._lastEvents);
      this._lastEvents = events;
    }

    // Only blip for edges the level gate can't voice (pulse already off). When
    // the beep is still on, _apply() opens the sustained gate and owns it.
    if (newEdges > 0 && !this.enabled) {
      this._blip(Number.isFinite(eventFreq) && eventFreq > 0 ? eventFreq : this.freq);
    } else {
      this._apply();
    }
  }

  // A single self-contained beep envelope (~65ms) for a pulse the level gate
  // missed. Click-free: fast attack, brief hold, short release.
  _blip(freq) {
    if (!this.ctx || !this.osc || !this.gain || this.muted) return;
    const now = this.ctx.currentTime;
    const hz = Math.min(12000, Math.max(20, freq));
    this.osc.frequency.setValueAtTime(hz, now);
    this.gain.gain.cancelScheduledValues(now);
    this.gain.gain.setTargetAtTime(this.volume, now, 0.002);      // attack
    this.gain.gain.setTargetAtTime(0, now + 0.055, 0.012);        // release
  }

  _apply() {
    if (!this.ctx || !this.osc || !this.gain) return;
    const now = this.ctx.currentTime;
    // Clamp to an audible, non-abusive band.
    const hz = Math.min(12000, Math.max(20, this.freq));
    this.osc.frequency.setValueAtTime(hz, now);
    const target = this.enabled && !this.muted ? this.volume : 0.0;
    this.gain.gain.cancelScheduledValues(now);
    // Short time-constant ramp avoids clicks on gate transitions.
    this.gain.gain.setTargetAtTime(target, now, 0.004);
  }
}
